import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { ROLE_RANK, type Role } from '@goss/types';
import { apiFetch } from '../lib/api.js';
import { useOrg } from '../app/OrgContext.js';
import { C, alpha, DESIRE_BY_KEY } from '../lib/theme.js';
import { Button, Card, ErrorLine, InfoTip, PageHeader } from '../ui/ui.js';
import { OrgPanel } from '../components/OrgPanel.js';
import { CreateOrgForm } from '../components/CreateOrgForm.js';
import { RegionTemplatesManager } from '../components/RegionTemplates.js';
import { AudienceBucketsManager } from '../components/AudienceBuckets.js';
import { LookalikeUploader } from '../components/LookalikeUploader.js';
import { ThemePicker } from '../app/ThemePicker.js';
import { CURRENCIES } from '../lib/currencies.js';
import type { Budget, FeatureRequest, Settings as SettingsData } from '../lib/types.js';

function Section({
  title,
  count,
  info,
  children,
}: {
  title: string;
  count?: number;
  info?: string;
  children: ReactNode;
}) {
  return (
    <section style={{ marginBottom: 30 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 12 }}>
        <div className="label-mono">{title}</div>
        {info && <InfoTip title={title} text={info} />}
        {count !== undefined && (
          <span style={{ font: `500 10px 'IBM Plex Mono',monospace`, color: C.faint }}>
            · {count}
          </span>
        )}
      </div>
      {children}
    </section>
  );
}

/** Confirm dialog for applying tenant defaults — spells out exactly what it writes. */
function ConfirmSeed({
  isEmpty,
  onConfirm,
  onCancel,
}: {
  isEmpty: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      onClick={onCancel}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,.62)',
        zIndex: 60,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        animation: 'basilfade .2s ease',
      }}
    >
      <div
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        style={{
          padding: 24,
          maxWidth: 460,
          width: '100%',
          background: C.surface,
          border: `1px solid ${C.line}`,
          borderRadius: 14,
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, color: C.primary, marginBottom: 10 }}>
          Apply Phuket Cleanse defaults?
        </div>
        <div style={{ fontSize: 13, color: C.secondary, lineHeight: 1.6, marginBottom: 14 }}>
          This writes the tenant&apos;s standard configuration:
        </div>
        <ul
          style={{
            margin: '0 0 14px',
            paddingLeft: 18,
            fontSize: 13,
            color: C.secondary,
            lineHeight: 1.7,
          }}
        >
          <li>Daily test budgets and the hard daily ceiling</li>
          <li>The target markets / regions with their age floors</li>
          <li>The four brand desires</li>
        </ul>
        <div
          style={{
            fontSize: 12.5,
            color: isEmpty ? C.muted : C.watch,
            marginBottom: 20,
            padding: '10px 12px',
            borderRadius: 8,
            background: alpha(isEmpty ? C.muted : C.watch, 10),
            border: `1px solid ${alpha(isEmpty ? C.muted : C.watch, 25)}`,
          }}
        >
          {isEmpty
            ? 'This workspace is empty, so nothing existing will be overwritten.'
            : 'This workspace already has settings — matching entries will be overwritten with the defaults. Your connected accounts and saved posts are not affected.'}
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <Button onClick={onCancel}>Cancel</Button>
          <Button variant="primary" onClick={onConfirm}>
            Apply defaults
          </Button>
        </div>
      </div>
    </div>
  );
}

/** A searchable dropdown (combobox) — used for the budget currency. */
function SearchSelect({
  value,
  onChange,
  options,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? options.filter((o) => `${o.value} ${o.label}`.toLowerCase().includes(needle))
    : options;
  const sel = options.find((o) => o.value === value);
  const close = () => {
    setOpen(false);
    setQ('');
  };
  return (
    <div style={{ position: 'relative' }}>
      <div
        className="field"
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          gap: 8,
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {sel ? `${sel.value} · ${sel.label}` : value || 'Select…'}
        </span>
        <span style={{ color: C.muted, fontSize: 11 }}>⌄</span>
      </div>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={close} />
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              left: 0,
              minWidth: 240,
              zIndex: 50,
              background: C.surface,
              border: `1px solid ${C.line}`,
              borderRadius: 10,
              boxShadow: '0 12px 32px rgba(0,0,0,.4)',
              overflow: 'hidden',
            }}
          >
            <input
              autoFocus
              className="field"
              placeholder="Search currency…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onClick={(e) => e.stopPropagation()}
              style={{ margin: 6, width: 'calc(100% - 12px)' }}
            />
            <div style={{ maxHeight: 220, overflowY: 'auto', padding: '0 5px 5px' }}>
              {filtered.map((o) => (
                <div
                  key={o.value}
                  onClick={() => {
                    onChange(o.value);
                    close();
                  }}
                  style={{
                    display: 'flex',
                    gap: 9,
                    alignItems: 'baseline',
                    padding: '8px 9px',
                    borderRadius: 7,
                    cursor: 'pointer',
                    background: o.value === value ? C.surface2 : 'transparent',
                  }}
                >
                  <span
                    style={{
                      font: `600 12px 'IBM Plex Mono',monospace`,
                      color: C.primary,
                      width: 38,
                    }}
                  >
                    {o.value}
                  </span>
                  <span style={{ fontSize: 12.5, color: C.muted }}>{o.label}</span>
                </div>
              ))}
              {filtered.length === 0 && (
                <div style={{ padding: '8px 9px', color: C.faint, fontSize: 12.5 }}>No match</div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function ListCard({ children }: { children: ReactNode }) {
  return <Card style={{ overflow: 'hidden' }}>{children}</Card>;
}

function Row({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 18px',
        borderBottom: `1px solid ${C.lineSoft}`,
        fontSize: 13,
        color: C.secondary,
      }}
    >
      {children}
    </div>
  );
}

type ReqStatus = 'open' | 'planned' | 'done' | 'dismissed';
const REQ_ACTIVE: ReqStatus[] = ['open', 'planned'];

/** Pill color per request status. */
function statusPill(status: string): { fg: string; bg: string; border: string } {
  switch (status) {
    case 'open':
      return { fg: C.basil, bg: alpha(C.basil, 12), border: alpha(C.basil, 40) };
    case 'planned':
      return { fg: C.watch, bg: alpha(C.watch, 12), border: alpha(C.watch, 40) };
    case 'done':
      return { fg: C.muted, bg: C.surface2, border: C.line };
    default: // dismissed
      return { fg: C.faint, bg: C.surface2, border: C.line };
  }
}

/** The feature-request / to-do backlog logged via Basil (the in-app assistant). */
function RequestsSection({ orgId, role }: { orgId: string; role: Role }) {
  const [reqs, setReqs] = useState<FeatureRequest[] | null>(null);
  const [showResolved, setShowResolved] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const canManage = ROLE_RANK[role] >= ROLE_RANK.marketer;

  useEffect(() => {
    let alive = true;
    apiFetch<{ requests: FeatureRequest[] }>(`/api/orgs/${orgId}/feature-requests`)
      .then((d) => alive && setReqs(d.requests))
      .catch(() => alive && setReqs([]));
    return () => {
      alive = false;
    };
  }, [orgId]);

  async function setStatus(id: string, status: ReqStatus) {
    setBusy(id);
    try {
      await apiFetch(`/api/orgs/${orgId}/feature-requests/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });
      setReqs((prev) => prev?.map((r) => (r.id === id ? { ...r, status } : r)) ?? prev);
    } catch {
      /* leave as-is on failure */
    } finally {
      setBusy(null);
    }
  }

  if (reqs === null) {
    return (
      <div style={{ color: C.muted, font: `500 12px 'IBM Plex Mono',monospace` }}>LOADING…</div>
    );
  }
  if (reqs.length === 0) {
    return (
      <Card style={{ padding: '14px 18px', color: C.muted, fontSize: 13 }}>
        No requests yet. When anyone asks Basil for a tweak or a new feature, they can log it here
        for review.
      </Card>
    );
  }

  const resolvedCount = reqs.filter((r) => !REQ_ACTIVE.includes(r.status as ReqStatus)).length;
  const visible = reqs
    .filter((r) => showResolved || REQ_ACTIVE.includes(r.status as ReqStatus))
    // Active first, then by newest.
    .sort((a, b) => {
      const aActive = REQ_ACTIVE.includes(a.status as ReqStatus) ? 0 : 1;
      const bActive = REQ_ACTIVE.includes(b.status as ReqStatus) ? 0 : 1;
      return aActive - bActive || (a.created_at < b.created_at ? 1 : -1);
    });

  const actionLink = (label: string, onClick: () => void, id: string) => (
    <span
      onClick={() => busy !== id && onClick()}
      style={{
        font: `600 11px 'IBM Plex Mono',monospace`,
        color: C.basil,
        cursor: busy === id ? 'default' : 'pointer',
        opacity: busy === id ? 0.5 : 1,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );

  return (
    <>
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        {visible.map((r, i) => {
          const resolved = !REQ_ACTIVE.includes(r.status as ReqStatus);
          const pill = statusPill(r.status);
          return (
            <div
              key={r.id}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '13px 16px',
                borderTop: i === 0 ? 'none' : `1px solid ${C.lineSoft}`,
                opacity: resolved ? 0.6 : 1,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 13.5,
                    fontWeight: 600,
                    color: C.primary,
                    textDecoration: r.status === 'dismissed' ? 'line-through' : 'none',
                  }}
                >
                  {r.title}
                </div>
                {r.detail && (
                  <div style={{ fontSize: 12.5, color: C.muted, marginTop: 3, lineHeight: 1.45 }}>
                    {r.detail}
                  </div>
                )}
                <div
                  style={{
                    display: 'flex',
                    gap: 14,
                    alignItems: 'center',
                    marginTop: 7,
                    flexWrap: 'wrap',
                  }}
                >
                  <span style={{ font: `500 10px 'IBM Plex Mono',monospace`, color: C.faint }}>
                    {r.source.toUpperCase()} · {r.created_at.slice(0, 10)}
                  </span>
                  {canManage && !resolved && (
                    <>
                      {actionLink('✓ Mark done', () => void setStatus(r.id, 'done'), r.id)}
                      {actionLink('Dismiss', () => void setStatus(r.id, 'dismissed'), r.id)}
                    </>
                  )}
                  {canManage &&
                    resolved &&
                    actionLink('↺ Reopen', () => void setStatus(r.id, 'open'), r.id)}
                </div>
              </div>
              <span
                style={{
                  padding: '3px 9px',
                  borderRadius: 999,
                  fontSize: 10.5,
                  fontWeight: 600,
                  letterSpacing: '.03em',
                  whiteSpace: 'nowrap',
                  color: pill.fg,
                  background: pill.bg,
                  border: `1px solid ${pill.border}`,
                }}
              >
                {r.status.toUpperCase()}
              </span>
            </div>
          );
        })}
      </Card>
      {resolvedCount > 0 && (
        <div
          onClick={() => setShowResolved((s) => !s)}
          style={{
            marginTop: 8,
            font: `600 11px 'IBM Plex Mono',monospace`,
            color: C.muted,
            cursor: 'pointer',
          }}
        >
          {showResolved ? '▾ Hide' : '▸ Show'} {resolvedCount} resolved
        </div>
      )}
    </>
  );
}

export function Settings() {
  const { orgId, activeOrg, reload } = useOrg();
  const [data, setData] = useState<SettingsData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [seeding, setSeeding] = useState(false);
  const [confirmSeed, setConfirmSeed] = useState(false);
  // Bump to remount the audience-buckets list after a lookalike upload.
  const [audRefresh, setAudRefresh] = useState(0);

  async function load() {
    if (!orgId) return;
    try {
      setData(await apiFetch<SettingsData>(`/api/orgs/${orgId}/settings`));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load settings');
    }
  }

  useEffect(() => {
    setData(null);
    void load();
  }, [orgId]);

  const canEdit = data ? ROLE_RANK[data.role] >= ROLE_RANK.admin : false;
  const isEmpty = data && !data.budget && data.markets.length === 0 && data.desires.length === 0;

  async function seed() {
    setConfirmSeed(false);
    setSeeding(true);
    setError(null);
    try {
      await apiFetch(`/api/orgs/${orgId}/seed/phuket-cleanse`, { method: 'POST' });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Seeding failed');
    } finally {
      setSeeding(false);
    }
  }

  return (
    <div style={{ padding: '26px 30px 60px', maxWidth: 900, animation: 'basilfade .3s ease' }}>
      <PageHeader
        eyebrow={
          activeOrg
            ? `${activeOrg.name} · ${activeOrg.default_currency} · ${activeOrg.timezone}`
            : 'Tenant configuration'
        }
        title="Settings"
        actions={
          canEdit ? (
            <Button variant="primary" onClick={() => setConfirmSeed(true)} disabled={seeding}>
              {seeding ? 'Applying…' : 'Apply Phuket Cleanse defaults'}
            </Button>
          ) : undefined
        }
      />
      {error && <ErrorLine>{error}</ErrorLine>}

      {confirmSeed && (
        <ConfirmSeed
          isEmpty={!!isEmpty}
          onConfirm={() => void seed()}
          onCancel={() => setConfirmSeed(false)}
        />
      )}

      {/* Appearance (personal, not tenant-scoped) */}
      <Section
        title="Appearance"
        info="The interface theme, saved to this browser only. Phuket Cleanse is locked to the Dim theme for brand consistency; other workspaces can choose any theme."
      >
        <Card
          style={{
            padding: 18,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
          }}
        >
          <div>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: C.primary }}>Theme</div>
            <div style={{ fontSize: 12.5, color: C.muted, marginTop: 2 }}>
              {activeOrg?.slug === 'phuket-cleanse'
                ? 'Locked to Dim for the Phuket Cleanse brand.'
                : 'Choose an interface theme. Saved to this browser only.'}
            </div>
          </div>
          {activeOrg?.slug === 'phuket-cleanse' ? (
            <span
              style={{
                padding: '7px 12px',
                borderRadius: 8,
                border: `1px solid ${C.line}`,
                color: C.secondary,
                font: `500 11.5px 'IBM Plex Mono',monospace`,
                letterSpacing: '.04em',
              }}
            >
              DIM · LOCKED
            </span>
          ) : (
            <ThemePicker />
          )}
        </Card>
      </Section>

      {!data && !error && (
        <div style={{ color: C.muted, font: `500 12px 'IBM Plex Mono',monospace` }}>LOADING…</div>
      )}

      {data && (
        <>
          {isEmpty && (
            <Card style={{ padding: '14px 18px', marginBottom: 24, color: C.muted, fontSize: 13 }}>
              No settings yet.{' '}
              {canEdit
                ? 'Apply Phuket Cleanse defaults to seed markets, budgets, and the four desires.'
                : 'Ask an admin to apply the tenant defaults.'}
            </Card>
          )}

          {/* Team */}
          {activeOrg && (
            <Section
              title="Team"
              info="People with access to this workspace and their role. Each role grants a different level of control — see Roles & permissions below."
            >
              <OrgPanel org={activeOrg} />
            </Section>
          )}

          {/* Requests & to-dos — logged via Basil (the in-app assistant) */}
          <Section
            title="Requests & to-dos"
            info="Tweaks and feature requests that anyone logged through Basil (the in-app assistant). Your build backlog to review."
          >
            <RequestsSection orgId={orgId!} role={data.role} />
          </Section>

          {/* Region templates */}
          <Section
            title="Region templates"
            info="Reusable audiences: a name, the angle to emphasize for that market (English only — the brand voice is kept), and its target cities. On any post, 'Tailor for a region' re-angles the copy for it and reuses its cities. Not translation — regional messaging."
          >
            <RegionTemplatesManager orgId={orgId!} canEdit={canEdit} />
          </Section>

          {/* Audience buckets */}
          <Section
            title="Audiences"
            info="Describe an audience in plain English and Basil builds the real Meta targeting (age, gender, cities, interests) as a reusable bucket. Or build a Lookalike from your guest list. Pick one at Launch to target beyond the locked standard. Meta's prohibited categories (health, body, race, religion, politics) can't be targeted — Basil warns and skips them."
          >
            {canEdit && (
              <LookalikeUploader orgId={orgId!} onDone={() => setAudRefresh((x) => x + 1)} />
            )}
            <AudienceBucketsManager key={audRefresh} orgId={orgId!} canEdit={canEdit} />
          </Section>

          {/* Budget */}
          <Section
            title="Budget"
            info="The default daily ad budgets and the hard daily ceiling that campaigns can't exceed without an explicit override. Money-safety guardrail."
          >
            <BudgetSection budget={data.budget} orgId={orgId!} canEdit={canEdit} onSaved={load} />
          </Section>

          {/* Desires */}
          <Section
            title="Desires — the four doors"
            count={data.desires.length}
            info="The four emotional 'doors' the Copy Engine writes toward — energy, calm, freedom and magic. Basil rotates them so ads don't lean on the same feeling twice running."
          >
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {data.desires.map((d) => {
                const color = DESIRE_BY_KEY[d.key] ?? C.muted;
                return (
                  <span
                    key={d.id}
                    className="pill"
                    style={{
                      color,
                      background: alpha(color, 13),
                      border: `1px solid ${alpha(color, 34)}`,
                      fontSize: 11,
                    }}
                  >
                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: color }} />
                    {d.label}
                    {d.weight !== 1 ? ` ·×${d.weight}` : ''}
                  </span>
                );
              })}
              {data.desires.length === 0 && (
                <span style={{ color: C.faint, fontSize: 13 }}>none</span>
              )}
            </div>
          </Section>

          {/* Markets */}
          <Section
            title="Markets"
            count={data.markets.length}
            info="The audiences/regions this brand targets, with their default age floor and audience mode. Feeds the campaign builder's targeting."
          >
            <ListCard>
              {data.markets.map((m) => (
                <Row key={m.id}>
                  <span style={{ color: C.primary, fontWeight: 500 }}>{m.name}</span>
                  <span style={{ color: C.muted }}>
                    {m.audience_mode} · age {m.age_min}+{m.winter_only ? ' · winter-only' : ''}
                  </span>
                  {!m.active && (
                    <span style={{ font: `500 10px 'IBM Plex Mono',monospace`, color: C.faint }}>
                      INACTIVE
                    </span>
                  )}
                </Row>
              ))}
              {data.markets.length === 0 && (
                <Row>
                  <span style={{ color: C.faint }}>none</span>
                </Row>
              )}
            </ListCard>
          </Section>

          {/* Holidays */}
          <Section
            title="Holiday calendar"
            count={data.holidays.length}
            info="Seasonal dates (including lunar ones) with a lead time, so campaigns can ramp ahead of key moments."
          >
            <ListCard>
              {data.holidays.map((h) => (
                <Row key={h.id}>
                  <span style={{ color: C.primary }}>{h.name}</span>
                  <span
                    style={{
                      marginLeft: 'auto',
                      font: `500 11px 'IBM Plex Mono',monospace`,
                      color: C.muted,
                    }}
                  >
                    {h.holiday_date ?? h.recurrence ?? '—'}
                    {h.is_lunar ? ' · lunar' : ''} · lead {h.lead_days}d
                  </span>
                </Row>
              ))}
              {data.holidays.length === 0 && (
                <Row>
                  <span style={{ color: C.faint }}>none</span>
                </Row>
              )}
            </ListCard>
          </Section>

          {/* Offers */}
          <Section
            title="Offers"
            count={data.offers.length}
            info="Promo codes used in ads. Each code is stamped onto a campaign's link so bookings can be attributed back to it for ROAS."
          >
            <ListCard>
              {data.offers.map((o) => (
                <Row key={o.id}>
                  <code style={{ font: `500 12px 'IBM Plex Mono',monospace`, color: C.basil }}>
                    {o.code}
                  </code>
                  <span style={{ color: C.muted }}>{o.description}</span>
                  {!o.active && (
                    <span style={{ font: `500 10px 'IBM Plex Mono',monospace`, color: C.faint }}>
                      INACTIVE
                    </span>
                  )}
                </Row>
              ))}
              {data.offers.length === 0 && (
                <Row>
                  <span style={{ color: C.faint }}>none</span>
                </Row>
              )}
            </ListCard>
          </Section>

          {/* Landing pages */}
          <Section
            title="Landing pages"
            count={data.landing_pages.length}
            info="The destination URLs ads route to, mapped to a desire door. The 'Learn More' CTA points here (the blog/route step, not a hard 'book now')."
          >
            <ListCard>
              {data.landing_pages.map((l) => (
                <Row key={l.id}>
                  <span style={{ color: C.primary, fontWeight: 500 }}>{l.desire_key}</span>
                  <span
                    style={{
                      font: `500 11px 'IBM Plex Mono',monospace`,
                      color: C.muted,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {l.url}
                  </span>
                  {!l.active && (
                    <span
                      style={{
                        marginLeft: 'auto',
                        font: `500 10px 'IBM Plex Mono',monospace`,
                        color: C.faint,
                      }}
                    >
                      INACTIVE
                    </span>
                  )}
                </Row>
              ))}
              {data.landing_pages.length === 0 && (
                <Row>
                  <span style={{ color: C.faint }}>none</span>
                </Row>
              )}
            </ListCard>
          </Section>

          {/* Create another org */}
          <Section
            title="New organization"
            info="Spin up another brand/workspace on Basil. It starts blank; apply its own defaults and connect its own accounts."
          >
            <CreateOrgForm onCreated={() => void reload()} />
          </Section>
        </>
      )}
    </div>
  );
}

function BudgetSection({
  budget,
  orgId,
  canEdit,
  onSaved,
}: {
  budget: Budget | null;
  orgId: string;
  canEdit: boolean;
  onSaved: () => Promise<void>;
}) {
  const [t12, setT12] = useState(String(budget?.test_budget_1_2_ads ?? 500));
  const [t34, setT34] = useState(String(budget?.test_budget_3_4_ads ?? 1000));
  const [ceiling, setCeiling] = useState(String(budget?.daily_ceiling ?? ''));
  const [currency, setCurrency] = useState(budget?.currency ?? 'THB');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setT12(String(budget?.test_budget_1_2_ads ?? 500));
    setT34(String(budget?.test_budget_3_4_ads ?? 1000));
    setCeiling(String(budget?.daily_ceiling ?? ''));
    setCurrency(budget?.currency ?? 'THB');
  }, [budget]);

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await apiFetch(`/api/orgs/${orgId}/budget`, {
        method: 'PATCH',
        body: JSON.stringify({
          test_budget_1_2_ads: t12,
          test_budget_3_4_ads: t34,
          daily_ceiling: ceiling,
          currency,
        }),
      });
      await onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  }

  const labelStyle: React.CSSProperties = {
    display: 'grid',
    gap: 6,
    font: `500 10px 'IBM Plex Mono',monospace`,
    color: C.muted,
    letterSpacing: '.1em',
  };
  const field = (
    label: string,
    value: string,
    set: (v: string) => void,
    type = 'number',
    info?: string,
  ) => (
    <label style={labelStyle}>
      <span style={{ display: 'flex', alignItems: 'center' }}>
        {label}
        {info && <InfoTip title={label} text={info} />}
      </span>
      <input className="field" value={value} onChange={(e) => set(e.target.value)} type={type} />
    </label>
  );

  if (!canEdit) {
    return (
      <Card style={{ padding: '14px 18px', color: C.secondary, fontSize: 13 }}>
        {budget
          ? `${budget.currency} ${budget.test_budget_1_2_ads} (1–2 ads) / ${budget.test_budget_3_4_ads} (3–4 ads) · ceiling ${budget.daily_ceiling ?? '—'}`
          : 'Not set.'}
      </Card>
    );
  }

  return (
    <Card style={{ padding: 20 }}>
      <form
        onSubmit={save}
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, minmax(0,1fr))',
          gap: 12,
          alignItems: 'end',
        }}
      >
        <div style={labelStyle}>
          <span style={{ display: 'flex', alignItems: 'center' }}>
            CURRENCY
            <InfoTip
              title="Currency"
              text="The currency all budgets and reporting for this workspace use. Search by code (e.g. THB) or name."
            />
          </span>
          <SearchSelect
            value={currency}
            onChange={setCurrency}
            options={CURRENCIES.map((c) => ({ value: c.code, label: c.name }))}
          />
        </div>
        {field(
          'TEST 1–2 ADS',
          t12,
          setT12,
          'number',
          'Default daily budget when a campaign is testing 1–2 ads.',
        )}
        {field(
          'TEST 3–4 ADS',
          t34,
          setT34,
          'number',
          'Default daily budget when a campaign is testing 3–4 ads.',
        )}
        {field(
          'DAILY CEILING',
          ceiling,
          setCeiling,
          'number',
          'The maximum daily spend a campaign can be built with. Going higher needs an explicit override on the Launch page.',
        )}
        <Button
          variant="primary"
          type="submit"
          disabled={busy}
          style={{ gridColumn: '1 / -1', justifySelf: 'start' }}
        >
          {busy ? 'Saving…' : 'Save budget'}
        </Button>
      </form>
      {error && <ErrorLine>{error}</ErrorLine>}
    </Card>
  );
}
